import React, { useState, ReactNode } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Divider,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  useTheme as useMuiTheme,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Link,
  Chip,
  alpha
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Route as RouteIcon,
  DirectionsCar as VehicleIcon,
  CalendarToday as CalendarIcon,
  EventNote as EventIcon,
  Assessment as AssessmentIcon,
  Summarize as SummarizeIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  ChevronRight as ChevronRightIcon,
  PinDrop as StopIcon,
  NotificationsActive as AlertIcon,
  Power as PowerIcon,
  AltRoute as GeofenceIcon,
  Map as MapIcon,
  MyLocation as MyLocationIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { UserProvider, useUser } from "../provider/UserProvider";
import { useTheme } from "../utlis/useTheme";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
      style={{ paddingTop: '24px' }}
    >
      {value === index && children}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `reports-tab-${index}`,
    'aria-controls': `reports-tabpanel-${index}`,
  };
};

const reportTypes = [
  {
    id: 'route',
    name: 'Position Reports',
    icon: <MapIcon />,
    description: 'Track vehicle positions and routes',
    path: '/reports/route',
    permission: 'ROUTE_REPORT'
  },
  {
    id: 'stop',
    name: 'Stop Reports',
    icon: <LocationOnIcon />,
    description: 'Track vehicle stops and durations',
    path: '/reports/stop',
    permission: 'STOP_REPORT'
  },
  {
    id: 'event',
    name: 'Event Reports',
    icon: <AlertIcon />,
    description: 'Monitor vehicle events and alerts',
    path: '/reports/event',
    permission: 'EVENT_REPORT'
  },
  {
    id: 'summary',
    name: 'Summary Reports',
    icon: <AssessmentIcon />,
    description: 'Get overview of fleet performance',
    path: '/reports/summary',
    permission: 'SUMMARY_REPORT'
  },
  {
    id: 'geofencing',
    name: 'Geofencing Reports',
    icon: <MyLocationIcon />,
    description: 'Monitor geofence entries and exits',
    path: '/reports/geofencing',
    permission: 'GEOFENCING_REPORT'
  },
  {
    id: 'ignition',
    name: 'Ignition Reports',
    icon: <PowerIcon />,
    description: 'Track vehicle ignition events',
    path: '/reports/ignition',
    permission: 'TRIP_REPORT'
  },
  {
    id: 'trip',
    name: 'Trip Reports',
    icon: <MapIcon />,
    description: 'Analyze trip details and statistics',
    path: '/reports/trip',
    permission: 'TRIP_REPORT'
  }
];

// Define interfaces for report data types
interface BaseReport {
  id: number;
  name: string;
  vehicle: string;
  dateRange: string;
  createdAt: string;
  format: string;
}

interface EventReport extends BaseReport {
  eventTypes: string[];
}

// Mock data for route reports
const routeReportData: BaseReport[] = [
  { id: 1, name: 'Daily Delivery Routes Analysis', vehicle: 'Multiple', dateRange: 'Last 7 days', createdAt: '2023-12-15', format: 'PDF' },
  { id: 2, name: 'Warehouse to Distribution Center Routes', vehicle: 'Truck Fleet', dateRange: 'Last 30 days', createdAt: '2023-12-01', format: 'Excel' },
  { id: 3, name: 'Optimal Route Comparison', vehicle: 'Delivery Vans', dateRange: 'Custom (Oct-Nov 2023)', createdAt: '2023-11-20', format: 'PDF' },
  { id: 4, name: 'City Center Delivery Routes', vehicle: 'Ford Transit', dateRange: 'Last 14 days', createdAt: '2023-11-15', format: 'Excel' },
  { id: 5, name: 'Highway Route Efficiency', vehicle: 'Long-haul Trucks', dateRange: 'Custom (Sep-Oct 2023)', createdAt: '2023-10-30', format: 'PDF' }
];

// Mock data for trip reports
const tripReportData: BaseReport[] = [
  { id: 1, name: 'Driver Performance Analysis', vehicle: 'All Vehicles', dateRange: 'Last 30 days', createdAt: '2023-12-10', format: 'PDF' },
  { id: 2, name: 'Fuel Consumption by Trip', vehicle: 'Delivery Fleet', dateRange: 'Last Quarter', createdAt: '2023-11-25', format: 'Excel' },
  { id: 3, name: 'Average Trip Duration', vehicle: 'Service Vehicles', dateRange: 'Last 60 days', createdAt: '2023-11-10', format: 'PDF' },
  { id: 4, name: 'Idle Time Analysis', vehicle: 'Toyota Camry', dateRange: 'Custom (Aug-Oct 2023)', createdAt: '2023-10-20', format: 'Excel' },
  { id: 5, name: 'Customer Visit Report', vehicle: 'Sales Team Vehicles', dateRange: 'Last Month', createdAt: '2023-10-05', format: 'PDF' }
];

// Mock data for summary reports
const summaryReportData: BaseReport[] = [
  { id: 1, name: 'Monthly Fleet Performance Overview', vehicle: 'All Vehicles', dateRange: 'Last Month', createdAt: '2023-12-05', format: 'PDF' },
  { id: 2, name: 'Quarterly Maintenance Summary', vehicle: 'All Vehicles', dateRange: 'Last Quarter', createdAt: '2023-10-10', format: 'Excel' },
  { id: 3, name: 'Annual Fuel Consumption Analysis', vehicle: 'All Vehicles', dateRange: 'Year 2023', createdAt: '2023-12-01', format: 'PDF' },
  { id: 4, name: 'Driver Behavior Summary', vehicle: 'All Vehicles', dateRange: 'Last 90 days', createdAt: '2023-11-15', format: 'Excel' },
  { id: 5, name: 'Vehicle Utilization Report', vehicle: 'Commercial Fleet', dateRange: 'Last 6 months', createdAt: '2023-09-28', format: 'PDF' }
];

// Mock data for stop reports
const stopReportData: BaseReport[] = [
  { id: 1, name: 'Daily Stop Duration Analysis', vehicle: 'Delivery Fleet', dateRange: 'Last 7 days', createdAt: '2023-12-16', format: 'PDF' },
  { id: 2, name: 'Unauthorized Stops Report', vehicle: 'All Vehicles', dateRange: 'Last 14 days', createdAt: '2023-12-10', format: 'Excel' },
  { id: 3, name: 'Customer Location Stops', vehicle: 'Sales Vehicles', dateRange: 'Last 30 days', createdAt: '2023-11-28', format: 'PDF' },
  { id: 4, name: 'Warehouse Loading Time Analysis', vehicle: 'Truck Fleet', dateRange: 'Current Month', createdAt: '2023-11-22', format: 'Excel' },
  { id: 5, name: 'POI Stop Frequency Report', vehicle: 'Service Vehicles', dateRange: 'Last Quarter', createdAt: '2023-11-15', format: 'PDF' }
];

// Mock data for event reports
const eventReportData: EventReport[] = [
  { id: 1, name: 'Engine Status Events', vehicle: 'All Vehicles', dateRange: 'Last 7 days', createdAt: '2023-12-18', format: 'PDF', eventTypes: ['Engine On', 'Engine Off'] },
  { id: 2, name: 'Geofencing Activity Log', vehicle: 'Delivery Fleet', dateRange: 'Last 14 days', createdAt: '2023-12-12', format: 'Excel', eventTypes: ['Geofence Enter', 'Geofence Exit'] },
  { id: 3, name: 'Driver Behavior Events', vehicle: 'All Vehicles', dateRange: 'Last 30 days', createdAt: '2023-12-05', format: 'PDF', eventTypes: ['Harsh Braking', 'Rapid Acceleration', 'Speeding'] },
  { id: 4, name: 'Maintenance Alert Events', vehicle: 'Truck Fleet', dateRange: 'Current Month', createdAt: '2023-11-25', format: 'Excel', eventTypes: ['Service Due', 'Engine Check', 'Tire Pressure'] },
  { id: 5, name: 'Complete Event Log', vehicle: 'Commercial Vehicles', dateRange: 'Last Quarter', createdAt: '2023-11-10', format: 'PDF', eventTypes: ['All Events'] }
];

interface IconProps {
  sx?: React.CSSProperties | any;
}

const Reports: React.FC = () => {
  const { getPrimaryColor, getPrimaryLightColor, getPrimaryDarkColor } = useTheme();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const { permissions } = useUser();

  // All hooks must be declared before any conditional returns
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState('last30');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Filter report types based on permissions
  const availableReportTypes = reportTypes.filter(type =>
    permissions?.includes(type.permission)
  );

  // If no report permissions, show error
  if (availableReportTypes.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 4,
            bgcolor: "white",
            border: "1px solid",
            borderColor: alpha(getPrimaryColor() || "#1D2B56", 0.2),
          }}
        >
          <Typography variant="h6" color="error" align="center">
            You don't have permission to access any reports.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const getCurrentReportData = () => {
    switch (tabValue) {
      case 0:
        return routeReportData;
      case 1:
        return tripReportData;
      case 2:
        return stopReportData;
      case 3:
        return eventReportData;
      case 4:
        return summaryReportData;
      default:
        return [];
    }
  };

  const filteredReports = getCurrentReportData().filter(report =>
    report.name.toLowerCase().includes(search.toLowerCase()) ||
    report.vehicle.toLowerCase().includes(search.toLowerCase()) ||
    report.dateRange.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedReports = filteredReports.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Helper function to check if a report is an EventReport
  const isEventReport = (report: BaseReport | EventReport): report is EventReport => {
    return 'eventTypes' in report;
  };

  return (
    <Box sx={{ bgcolor: alpha(getPrimaryColor() || "#1D2B56", 0.02), minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(getPrimaryColor() || "#1D2B56", 0.05)} 0%, ${alpha(getPrimaryColor() || "#1D2B56", 0)} 100%)`,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${getPrimaryColor() || "#1D2B56"} 0%, ${getPrimaryLightColor() || "#2a3a6a"} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1
            }}
          >
            Reports & Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Generate and analyze detailed reports for your fleet operations
          </Typography>
        </Box>

        {/* Quick Report Templates */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#1e293b",
            }}
          >
            <AssessmentIcon sx={{ color: getPrimaryColor() || "#1D2B56" }} />
            Available Report Types
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)'
            },
            gap: 3
          }}>
            {availableReportTypes.map((type, index) => (
              <Card
                key={type.id}
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: alpha(getPrimaryColor() || "#1D2B56", 0.1),
                  background: "white",
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${alpha(getPrimaryColor() || "#1D2B56", 0.1)}`,
                    '& .hover-gradient': {
                      opacity: 1,
                    },
                    '& .card-content': {
                      background: `linear-gradient(135deg, ${alpha(getPrimaryColor() || "#1D2B56", 0.05)} 0%, ${alpha(getPrimaryColor() || "#1D2B56", 0.02)} 100%)`,
                    }
                  }
                }}
              >
                <Box
                  className="hover-gradient"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${getPrimaryColor() || "#1D2B56"}, ${getPrimaryLightColor() || "#2a3a6a"})`,
                    opacity: 0,
                    transition: 'opacity 0.3s ease'
                  }}
                />
                <CardContent
                  className="card-content"
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'background 0.3s ease'
                  }}
                >
                  <Box
                    sx={{
                      mb: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: `linear-gradient(135deg, ${alpha(getPrimaryColor() || "#1D2B56", 0.1)} 0%, ${alpha(getPrimaryColor() || "#1D2B56", 0.05)} 100%)`,
                      color: getPrimaryColor() || "#1D2B56",
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'rotate(5deg)',
                      }
                    }}
                  >
                    {React.cloneElement(type.icon as React.ReactElement<IconProps>, {
                      sx: { fontSize: 28 }
                    })}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: '#1e293b'
                    }}
                  >
                    {type.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      mb: 3,
                      flex: 1
                    }}
                  >
                    {type.description}
                  </Typography>
                  <Button
                    onClick={() => navigate(type.path)}
                    variant="contained"
                    endIcon={<ChevronRightIcon />}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      bgcolor: getPrimaryColor() || "#1D2B56",
                      color: 'white',
                      '&:hover': {
                        bgcolor: getPrimaryLightColor() || "#2a3a6a",
                      },
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

      </Container>
    </Box>
  );
};

export default Reports; 